import React, {FC, useState, useEffect} from "react"

import {Col, Row} from "react-bootstrap"
import Card from "@irati/ui/src/Layout/Card"
import {IconTooltip} from "@irati/ui/src/Tooltip"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import {FormattedMessage, FormattedCurrency} from "@irati/utils/lang"
import {Redirect} from "react-router-dom"
import {Tax, Topup} from "@irati/types"
import topupService from "@irati/wallet/services/TopUp"
import {getDomain} from "@irati/utils/links"
import authentication from "@irati/auth"
import qs from "qs"
import endpoints from "@irati/payments/endpoints"
import History from "@irati/utils/history"
import PropTypes from "prop-types"
import Flex from "@irati/ui/src/Flex"
import {Hr} from "@irati/ui/src/Layout"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {TopUpTypes} from "@irati/wallet/enums"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import Loading from "@irati/ui/src/Loading"
import {hangPromise} from "@irati/utils/promise"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {ServiceFeeValue, TopUpOnChange, TopUpPayload} from "../../../containers/AdminReward/AdminRewardTopUp"
import ButtonTabs from "../ButtonTabs"
import ConfirmationModal from "./ConfirmationModal"
import ErrorCard from "./ErrorCard"
import Intro from "./Intro"
import BankTransfer from "./BankTransfer"
import PayByCard from "./PayByCard"
import ServiceFeeOfferCard from "./ServiceFeeOfferCard"
import {getTaxCost} from "@shared/payments/utils/currencies"
import {useBudgetsList} from "@irati/wallet/hooks/budget"

const BANK_TRANSFER = "bankTransfer"
const PAY_BY_CARD = "payByCard"

interface Props {
  topupAmount: number
  totalToPay: number
  payload: TopUpPayload
  rewardWalletTopup: Topup
  createTopUp: () => Promise<void>
  onPayloadChange: TopUpOnChange
  serviceFeeValue: ServiceFeeValue
}

const Checkout: FC<Props> = (
  {payload, topupAmount, totalToPay, rewardWalletTopup, createTopUp, onPayloadChange, serviceFeeValue},
  context
) => {
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [current, setCurrent] = useState(BANK_TRANSFER)
  const {load, finalCost, taxCost, tax} = useTax(totalToPay)
  const {refetch: refetchBudgets} = useBudgetsList()

  const {purchaseCurrency: currencyCode} = payload

  const handleCardError = () => {
    setIsError(true)
    tracking.payByCardError()
  }

  const goBackToTransactions = () => {
    History.push(
      {
        pathname: "/flexi-reward"
      },
      context
    )
  }

  const handleTryAgain = () => {
    History.push({
      pathname: "/wallet/top-up",
      rewardWalletTopup
    })
  }

  const getTopupStatus = () => {
    return topupService
      .complete(rewardWalletTopup.uuid)
      .then(() => {
        refetchBudgets()
        setShowCompletionModal(true)
        tracking.payByCardSuccess()
      })
      .catch(handleCardError)
  }

  const getPaymentRedirectUrl = () => {
    const domain = getDomain(authentication.userTenant().subdomain)
    const query = qs.stringify({
      redirectTo: `${domain}/wallet/top-up/confirm/${rewardWalletTopup.uuid}`
    })
    return `${endpoints.adyenRedirectCallbackUrl()}?${query}`
  }

  const theme = useTheme()
  const setPayWithCard = () => {
    onPayloadChange({...payload, type: TopUpTypes.PayWithCard})
    setCurrent(PAY_BY_CARD)
  }

  const setBankTransfer = () => {
    onPayloadChange({...payload, type: TopUpTypes.BankTransfer})
    setCurrent(BANK_TRANSFER)
  }

  const handlePaymentType = (id: string) => (id === BANK_TRANSFER ? setBankTransfer() : setPayWithCard())

  const hasServiceFeeDiscount = serviceFeeValue.tierServiceFeeAmount !== serviceFeeValue.baseServiceFeeAmount

  return (
    <Row>
      <Col md={12} lg={5}>
        <Intro />
        <Card marginBottom={4}>
          <Loading load={load}>
            <Flex gap={3} direction="column">
              <H4>
                <FormattedMessage id="wallet.reward.admin.summary.title" />
              </H4>

              <Hr flush />

              <ServiceFeeOfferCard />

              <Flex alignItems="center" justifyContent="space-between">
                <Flex>
                  <Icon icon={IconTypes.rewardPoints} size={24} />
                  <Body marginLeft>{payload.rpPoints}</Body>
                </Flex>
                <Body color="ink">
                  <FormattedCurrency value={topupAmount} currency={currencyCode} />
                </Body>
              </Flex>

              <Flex alignItems="center" justifyContent="space-between">
                <Flex gap={0.5}>
                  <Body color="ink">
                    <FormattedMessage id="wallet.reward.admin.card.topup.serviceFee" />
                  </Body>
                  <IconTooltip icon="i">
                    <FormattedMessage id="wallet.reward.admin.card.topup.tooltipBody" />
                  </IconTooltip>
                </Flex>

                <Flex gap={0.5}>
                  <Body textDecoration={hasServiceFeeDiscount ? "line-through" : undefined} color={"ink"}>
                    <FormattedCurrency value={serviceFeeValue.baseServiceFeeAmount} currency={currencyCode} />
                  </Body>
                  {hasServiceFeeDiscount && (
                    <Body color={theme.colors.tertiary.g_600}>
                      <FormattedCurrency value={serviceFeeValue.tierServiceFeeAmount} currency={currencyCode} />
                    </Body>
                  )}
                </Flex>
              </Flex>

              <Hr flush />

              <Flex alignItems="center" justifyContent="space-between">
                <Body color="ink">
                  <FormattedMessage id="wallet.reward.admin.card.topup.netAmount" />
                </Body>
                <Body color="ink">
                  <FormattedCurrency value={totalToPay} currency={currencyCode} />
                </Body>
              </Flex>

              <Flex alignItems="center" justifyContent="space-between">
                <Body color="ink">
                  {tax?.name} ({tax?.rate}%)
                </Body>
                <Body color="ink">
                  <FormattedCurrency value={taxCost} currency={currencyCode} />
                </Body>
              </Flex>

              <Hr flush />

              <Flex alignItems="center" justifyContent="space-between">
                <Body color="ink">
                  <FormattedMessage id="wallet.reward.admin.card.topup.totalToPay" />
                </Body>
                <Body color="ink">
                  <FormattedCurrency value={finalCost} currency={currencyCode} />
                </Body>
              </Flex>
            </Flex>
          </Loading>
        </Card>
      </Col>

      <Col md={12} lg={7}>
        <Card>
          <H2 marginBottom={2}>
            <FormattedMessage id="wallet.reward.admin.card.checkout.title" />
          </H2>

          <ButtonTabs
            buttons={[
              {id: BANK_TRANSFER, title: <FormattedMessage id="wallet.reward.admin.paymentMethod.bank" />},
              {id: PAY_BY_CARD, title: <FormattedMessage id="wallet.reward.admin.paymentMethod.card" />}
            ]}
            selected={current}
            onClick={handlePaymentType}
          />

          {current === BANK_TRANSFER && (
            <BankTransfer
              finalCost={finalCost}
              payload={payload}
              onPayloadChange={onPayloadChange}
              rewardWalletTopup={rewardWalletTopup}
              topupAmount={topupAmount}
              createTopUp={createTopUp}
              goBackToTransactions={goBackToTransactions}
            />
          )}

          {current === PAY_BY_CARD && (
            <PayByCard
              finalCost={finalCost}
              createTopUp={createTopUp}
              rewardWalletTopup={rewardWalletTopup}
              getTopupStatus={getTopupStatus}
              getPaymentRedirectUrl={getPaymentRedirectUrl}
            />
          )}

          {!rewardWalletTopup && <Redirect to="wallet/top-up" />}
          {isError && <ErrorCard goBackToTransactions={goBackToTransactions} handleTryAgain={handleTryAgain} />}
          {showCompletionModal && (
            <ConfirmationModal
              finalCost={finalCost}
              rewardPoints={payload.rpPoints}
              rewardWalletTopup={rewardWalletTopup}
              goBackToTransactions={goBackToTransactions}
              showCompletionModal={showCompletionModal}
            />
          )}
        </Card>
      </Col>
    </Row>
  )
}

Checkout.contextTypes = {
  appPath: PropTypes.string
}

interface TaxInfo extends Tax {
  percentage: number
}

function useTax(totalToPay: number) {
  const [tax, setTax] = useState<TaxInfo | null>(null)
  const [load, setLoad] = useState(hangPromise())
  const {taxCost, totalCost} = getTaxCost({totalBeforeTax: totalToPay, tax: tax?.rate})

  useEffect(() => {
    setLoad(topupService.getTaxForTenant().then(({name, rate}) => setTax({name, rate, percentage: rate / 100})))
  }, [])

  return {load, finalCost: totalCost, taxCost, tax}
}

export default Checkout
